import React, { Children, ReactNode } from 'react';
import { PortableText, PortableTextReactComponents } from '@portabletext/react';
import NextImage from '../nextImage/NextImage';
import imageHelper from '@/src/lib/helpers/imageHelper';
import styles from './PortableText.module.scss';
import { getRel } from '@/src/lib/utils/getRel';
import { isBlank } from '@/src/lib/utils/isBlank';

const portableCaption: Partial<PortableTextReactComponents> = {
  block: {
    normal: ({ children }) => {
      return (
        <div className={styles.caption__wrapper}>
          <p className={styles.caption__p}>{children}</p>
        </div>
      );
    },
  },
  marks: {
    strong: ({ children }: { children: ReactNode }) => <strong>{children}</strong>,
    em: ({ children }: { children: ReactNode }) => <em>{children}</em>,
  },
};

const PortableImage = ({ value }: any) => {
  const hasSizeOption = !!value.imageSize || !!value.customImageSize;
  const isSVG = !!value?.asset?._ref?.endsWith('svg');
  let maxImageSize = isSVG ? '400' : '972';

  if (!value.asset) {
    return null;
  }

  if (value.imageSize && value.imageSize !== 'default') {
    maxImageSize = value.imageSize;
  }

  if (value.customImageSize) {
    maxImageSize = value.customImageSize.toString();
  }

  const inlineStyles = hasSizeOption
    ? {
        width: '100%',
        maxWidth: `${maxImageSize}px`,
      }
    : undefined;

  return (
    <div className={styles.image__container}>
      <NextImage
        src={imageHelper(value).url}
        alt={imageHelper(value).alt ?? 'Image description'}
        className={`${styles.postContentImage} ${isSVG ? styles.svgImage : ''}`}
        fill={true}
        loading={'lazy'}
        sizes={`(max-width: 768px) 100vw, ${maxImageSize}px`}
        style={inlineStyles}
      />
      {value?.caption && <PortableText value={value?.caption} components={portableCaption} />}
    </div>
  );
};

const childrenToText = (children: ReactNode): string => {
  return Children.toArray(children)
    .map((child) => {
      if (typeof child === 'string') return child;
      if (typeof child === 'number') return String(child);
      if (React.isValidElement<{ children?: ReactNode }>(child)) {
        return childrenToText(child.props.children);
      }
      return '';
    })
    .join('');
};

const toSlug = (children: ReactNode): string =>
  childrenToText(children).toLowerCase().replace(/ /g, '-');

const heading = (Tag: 'h1' | 'h2' | 'h3' | 'h4') => {
  const Component = ({ children }: { children?: ReactNode }) => {
    const text = childrenToText(children);
    if (!text) return null;
    return <Tag id={toSlug(children)}>{children}</Tag>;
  };
  Component.displayName = Tag;
  return Component;
};

const serializers: Partial<PortableTextReactComponents> = {
  types: {
    image: ({ value }) => {
      return <PortableImage value={value} />;
    },
  },
  block: {
    h1: heading('h1'),
    h2: heading('h2'),
    h3: heading('h3'),
    h4: heading('h4'),
    normal: ({ children }) => {
      return <p>{children}</p>;
    },
  },
  marks: {
    strong: ({ children }: { children: ReactNode }) => <strong>{children}</strong>,
    em: ({ children }: { children: ReactNode }) => <em>{children}</em>,
    link: ({ value, children }) => {
      if (!value?.url) return null;

      const rel = getRel(value.relOverride, value.url);
      const target = isBlank(value.url, value.isBlank) ? '_blank' : undefined;
      return (
        <a className={styles.postLink} href={value.url} rel={rel} target={target}>
          {children}
        </a>
      );
    },
    featuredLink: ({ value, children }) => {
      if (!value?.url) return null;

      const rel = getRel(value.relOverride, value.url);
      const target = isBlank(value.url, value.isBlank) ? '_blank' : undefined;
      return (
        <a className={styles.featured__link} href={value.url} rel={rel} target={target}>
          <span className={styles.animated__container}>
            <span className={styles.animated__wrapper}>
              <span className={`${styles.arrow} ${styles.arrow__left}`}>
                <svg width="20" height="20" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M8 6L12 10L8 14"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <span className={styles.animated__text}>{children}</span>
              <span className={`${styles.arrow} ${styles.arrow__right}`}>
                <svg width="20" height="20" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M8 6L12 10L8 14"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </span>
          </span>
        </a>
      );
    },
    assetReference: ({ children, value }) => {
      // The file reference in the asset object has the form <_file>-<id>-<extension>
      // We split the text string to get the individual pieces of information.
      const [_file, id, extension] = value.file.asset._ref.split('-');
      // The URL to access your file should be of the form
      // https://cdn.sanity.io/files/{PROJECT_ID}/{DATASET}/{FILE ID}.{FILE EXTENSION}
      return (
        <a
          className={styles.postLink}
          href={`https://cdn.sanity.io/files/${process.env.NEXT_PUBLIC_SANITY_PROJECT_ID}/${process.env.NEXT_PUBLIC_SANITY_DATASET}/${id}.${extension}?dl=${id}.${extension}`}
          download>
          {children}
        </a>
      );
    },
  },
};

const PortableBlock: React.FC<any> = ({ value }) => {
  return <PortableText value={value} components={serializers} />;
};

export default PortableBlock;
